/**
 * Domain hook for Maintenance Jobs - Anchor Mode v2.0 compliant
 * FT-182 v9: Rewrite using useStorageState pattern (matches crew)
 *
 * Base key: 'maintenance_jobs' (vessel scoping handled by useStorageState)
 * RBAC-gated mutations
 * No vesselId in records
 * One write per save
 *
 * FIXES race condition where:
 * - Old pattern: storage.writeJSON() + event dispatch
 * - Event listeners read stale React state and overwrite localStorage
 *
 * NEW pattern:
 * - useStorageState atomically updates localStorage AND React state
 * - No event dispatching needed - React re-renders automatically
 *
 * @example
 * const { list, add, update, archive, restore, getById } = useMaintenanceJobsDomain();
 */

import { useMemo, useCallback } from 'react';
import { useStorageState } from '../../useStorageState';
import { usePermissions } from '../../usePermissions';
import { devLog } from '../../../utils/devLog';
import { track1 } from '../../../utils/track1Telemetry';

// RBAC multi-signature shim (same pattern as crew)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

// ID generator
const makeId = () => `MNT-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// Status normalizer
const normStatus = (s) => (s ? String(s).trim().toLowerCase() : 'active');

/**
 * Helper: Check if job is archived
 */
export const isArchived = (job) => {
  if (!job) return true;
  const status = (job.status || 'active').toLowerCase();
  return status === 'archived';
};

/**
 * Helper: Check if job is active (not archived)
 */
export const isActive = (job) => !isArchived(job);

/**
 * Write-time normalizer for job input
 */
export const normalizeJobInput = (input = {}) => {
  const now = Date.now();
  const name = (input.name || input.item || '').toString().trim();

  if (!name) {
    throw new Error('[useMaintenanceJobsDomain] Refusing to save job without a name');
  }

  return {
    id: input.id || makeId(),
    name,                               // Canonical name
    item: name,                         // Legacy compatibility
    category: input.category || '',
    domain: input.domain || '',
    frequency: input.frequency || '',
    // Detail fields — FT-293: whitelist expanded to preserve user-entered detail on CREATE
    // (EDIT path already preserves these via update() spread; this brings CREATE in line)
    purpose: input.purpose || '',
    scope: input.scope || '',
    requirement: input.requirement || '',
    notes: input.notes || '',
    interval: (typeof input.interval === 'number' && Number.isFinite(input.interval)) ? input.interval : null,
    unit: input.unit || 'days',
    // description as object (FT-144): prefer explicit object, else build from flat, else empty object
    description: (input.description && typeof input.description === 'object')
      ? { purpose: input.description.purpose || '', scope: input.description.scope || '' }
      : { purpose: input.purpose || '', scope: input.scope || '' },
    nextDue: input.nextDue || null,
    defaultDue: input.defaultDue || null,
    initialNextDue: input.initialNextDue || null,
    custom: input.custom ?? true,       // Default to custom (user-created)
    status: normStatus(input.status),
    // Completions array for history
    completions: Array.isArray(input.completions) ? input.completions : [],
    // FT-209: Defect↔Job linkage
    // jobType: 'routine' (custom, no defect) | 'scheduled' (system/regulatory) | 'corrective' (from defect)
    jobType: input.jobType || 'scheduled',
    // isOneTime: true for corrective jobs that don't recur
    isOneTime: input.isOneTime ?? false,
    // originatingDefectId: ID of defect that caused this corrective job to be created
    // NOTE: Different from completion.linkedDefectId which is defect found DURING maintenance
    originatingDefectId: input.originatingDefectId || null,
    // FT-183: Optional planning assignment (free text)
    assignedTo: input.assignedTo || null,
    // FT-48 / FT-255: Equipment linking — preserved on create. Without this,
    // normalizeJobInput silently drops the field on add() (caller's input is
    // re-shaped via this explicit field list). Users had to edit + re-link
    // after creation to make the link stick.
    linkedEquipmentIds: Array.isArray(input.linkedEquipmentIds) ? input.linkedEquipmentIds : [],
    // Audit trail
    createdBy: input.createdBy ?? null,
    createdAt: Number.isFinite(input.createdAt) ? input.createdAt : now,
    updatedBy: input.updatedBy ?? null,
    updatedAt: now,
    // Archive metadata
    archivedAt: input.archivedAt || null,
    archivedBy: input.archivedBy || null,
    // ⛔ no vesselId here – storage layer scopes per vessel
  };
};

/**
 * Read-time normalizer (lenient - handles legacy data)
 */
const normalizeJobRecord = (r = {}) => {
  const name = (r.name ?? r.item ?? '').trim();

  // Track 1: detect the "regen bomb" - id fallback via makeId() causes a fresh
  // MNT-<ts>-<rnd> id on every read for records with a falsy .id field.
  // Cheap conditional; module-singleton call, no React coupling.
  if (!r.id) {
    track1.log('[NORMALIZE-FALLBACK]', {
      hadName: !!name,
      keys: Object.keys(r).slice(0, 12),
    });
  }

  return {
    ...r,
    id: r.id || makeId(),
    name: name || 'Unnamed Job',
    item: name || 'Unnamed Job',
    status: normStatus(r.status),
    completions: Array.isArray(r.completions) ? r.completions : [],
    // FT-209: Read-time defaults for new fields (backward compatibility)
    jobType: r.jobType || 'scheduled',
    isOneTime: r.isOneTime ?? false,
    originatingDefectId: r.originatingDefectId || null,
  };
};

/**
 * Legacy normalizer for storage data
 */
const normalizeLegacy = (data) => {
  if (!data) return [];
  if (!Array.isArray(data)) {
    // Handle wrapped format { jobs: [...] }
    if (data.jobs && Array.isArray(data.jobs)) {
      return data.jobs.map(normalizeJobRecord);
    }
    return [];
  }
  return data.map(normalizeJobRecord);
};

/**
 * Hook for managing maintenance jobs
 */
export const useMaintenanceJobsDomain = () => {
  const { can, currentUser } = usePermissions();
  const userName = currentUser?.name || 'Unknown';

  // Vessel-scoped storage via useStorageState
  // Key format: maintenance_jobs (useStorageState handles vessel scoping)
  const { value: rawJobs, setValue, loaded } = useStorageState('maintenance_jobs', []);

  // Normalized list of active jobs (excludes archived)
  const list = useMemo(() => {
    const normalized = normalizeLegacy(rawJobs);
    return normalized.filter(isActive);
  }, [rawJobs]);

  // All jobs including archived (for admin views)
  const allJobs = useMemo(() => {
    return normalizeLegacy(rawJobs);
  }, [rawJobs]);

  // Archived jobs only
  const archivedJobs = useMemo(() => {
    const normalized = normalizeLegacy(rawJobs);
    return normalized.filter(isArchived);
  }, [rawJobs]);

  // ============================================================================
  // MUTATIONS (all use setValue for atomic updates)
  // ============================================================================

  /**
   * Add a new job - RBAC-gated
   */
  const add = useCallback((input) => {
    if (!canDo(can, 'maintenance', 'create')) {
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Permission denied for create');
      }
      return null;
    }

    try {
      const newJob = normalizeJobInput({
        ...input,
        createdBy: userName,
        updatedBy: userName,
      });

      // ONE write per save - atomic update
      setValue(prev => {
        const current = normalizeLegacy(prev);
        return [...current, newJob];
      });

      // Track 1: hydration-epoch bump - see update() for rationale.
      track1.bumpHydrationEpoch();

      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Added job:', newJob.id);
      }

      return newJob;
    } catch (err) {
      console.error('[useMaintenanceJobsDomain] Add failed:', err);
      return null;
    }
  }, [can, setValue, userName]);

  /**
   * Update an existing job - RBAC-gated
   */
  const update = useCallback((id, updates) => {
    if (!canDo(can, 'maintenance', 'update')) {
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Permission denied for update');
      }
      return false;
    }

    try {
      const now = Date.now();

      // Track 1: capture pre-state for [SILENT-DROP] + [REGEN-DETECT].
      let foundMatch = false;
      let preHashes = null;
      let postHashes = null;
      let preLen = 0;
      let postLen = 0;

      // ONE write per save - atomic update
      setValue(prev => {
        const current = normalizeLegacy(prev);
        preLen = current.length;
        preHashes = current.slice(0, 3).map(j => track1.hashId(j.id));
        const next = current.map(j => {
          if (j.id !== id) return j;
          foundMatch = true;
          return {
            ...j,
            ...updates,
            updatedBy: userName,
            updatedAt: now,
          };
        });
        postLen = next.length;
        postHashes = next.slice(0, 3).map(j => track1.hashId(j.id));
        return next;
      });

      // Track 1: hydration-epoch bump - feeds [STALE-EDITING-JOB] detection
      // at PlannedMaintenanceTab.js save site. update() reshapes the array
      // (even when foundMatch=false the array reference could be replaced by
      // normalizeLegacy re-mapping) so the epoch bump is unconditional.
      track1.bumpHydrationEpoch();

      // Track 1: [SILENT-DROP] - the exact class Investigator A hypothesised.
      // Cheap check; no behaviour change; return value unchanged.
      if (!foundMatch) {
        track1.log('[SILENT-DROP]', {
          hookName: 'useMaintenanceJobsDomain.update',
          idHash: track1.hashId(id),
          preLen,
          preHashes,
          updateKeys: Object.keys(updates || {}),
        });
        // FT-379: always-on anomaly path. Only PII-safe identifiers — the
        // update payload / key names are NOT shipped (candidate silent drop).
        try {
          track1.logAnomaly('[SILENT-DROP]', {
            collection: 'maintenance_jobs',
            id,
            reason: 'update-no-match',
          });
        } catch (_) { /* non-fatal */ }
      }

      // Track 1: [REGEN-DETECT] - if the leading id hashes shifted on an
      // operation that shouldn't renumber (update should preserve order + ids),
      // that's evidence of the SL-M##-timestamp regeneration hazard.
      if (preHashes && postHashes &&
          preHashes.join('|') !== postHashes.join('|')) {
        track1.log('[REGEN-DETECT]', {
          hookName: 'useMaintenanceJobsDomain.update',
          preLen, postLen, preHashes, postHashes,
        });
      }

      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Updated job:', id);
      }

      return true;
    } catch (err) {
      console.error('[useMaintenanceJobsDomain] Update failed:', err);
      return false;
    }
  }, [can, setValue, userName]);

  /**
   * Archive a job (soft delete) - RBAC-gated
   * FT-182: This is the key function that was broken with event-based approach
   */
  const archive = useCallback((id) => {
    if (!canDo(can, 'maintenance', 'update')) {
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Permission denied for archive');
      }
      return false;
    }

    const now = Date.now();

    // ONE write per save - atomic update via useStorageState
    // This is THE FIX: setValue atomically updates localStorage + React state
    // No race condition because there's no event dispatch/listener
    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(j => j.id === id
        ? {
            ...j,
            status: 'archived',
            archivedAt: new Date().toISOString(),
            archivedBy: userName,
            updatedAt: now
          }
        : j
      );
    });

    // Track 1: hydration-epoch bump.
    track1.bumpHydrationEpoch();

    if (process.env.NODE_ENV !== 'production') {
      devLog('[useMaintenanceJobsDomain] Archived job:', id);
    }

    return true;
  }, [can, setValue, userName]);

  /**
   * Restore an archived job - RBAC-gated
   */
  const restore = useCallback((id) => {
    if (!canDo(can, 'maintenance', 'update')) {
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Permission denied for restore');
      }
      return false;
    }

    const now = Date.now();

    // ONE write per save - atomic update
    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(j => j.id === id
        ? {
            ...j,
            status: 'active',
            archivedAt: null,
            archivedBy: null,
            updatedAt: now
          }
        : j
      );
    });

    // Track 1: hydration-epoch bump.
    track1.bumpHydrationEpoch();

    if (process.env.NODE_ENV !== 'production') {
      devLog('[useMaintenanceJobsDomain] Restored job:', id);
    }

    return true;
  }, [can, setValue, userName]);

  /**
   * Permanently delete a job - RBAC-gated (admin only)
   */
  const remove = useCallback((id) => {
    if (!canDo(can, 'maintenance', 'delete')) {
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Permission denied for delete');
      }
      return false;
    }

    // ONE write per save - atomic update
    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.filter(j => j.id !== id);
    });

    // Track 1: hydration-epoch bump.
    track1.bumpHydrationEpoch();

    if (process.env.NODE_ENV !== 'production') {
      devLog('[useMaintenanceJobsDomain] Removed job:', id);
    }

    return true;
  }, [can, setValue]);

  /**
   * FT-353f: Clear the originatingDefectId back-reference on a job.
   * Called when the linked defect is deleted — the job itself must NOT
   * be removed (audit trail preservation) and the corrective jobType is
   * left intact (the job's corrective nature was true at creation
   * time). Only the reverse pointer is nulled so the job card + detail
   * view stop showing a stale "→ DEF-xxx" chip.
   *
   * Skips the update entirely if the job isn't found or already has no
   * back-reference so callers can invoke unconditionally without
   * spamming updates.
   */
  const clearOriginatingDefectId = useCallback((jobId) => {
    if (!jobId) return false;

    if (!canDo(can, 'maintenance', 'update')) {
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Permission denied for clearOriginatingDefectId');
      }
      return false;
    }

    let didUpdate = false;
    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(j => {
        if (j.id !== jobId) return j;
        if (!j.originatingDefectId) return j;
        didUpdate = true;
        return {
          ...j,
          originatingDefectId: null,
          updatedBy: userName,
          updatedAt: Date.now(),
        };
      });
    });

    if (didUpdate) {
      track1.bumpHydrationEpoch();
      if (process.env.NODE_ENV !== 'production') {
        devLog('[useMaintenanceJobsDomain] Cleared originatingDefectId on job:', jobId);
      }
    }
    return didUpdate;
  }, [can, setValue, userName]);

  /**
   * Add a completion record to a job
   */
  const addCompletion = useCallback((jobId, completion) => {
    if (!canDo(can, 'maintenance', 'update')) {
      return false;
    }

    const now = Date.now();
    const completionRecord = {
      id: `CMP-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      completedAt: new Date().toISOString(),
      completedBy: userName,
      ...completion,
    };

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(j => {
        if (j.id !== jobId) return j;
        return {
          ...j,
          completions: [...(j.completions || []), completionRecord],
          nextDue: completion.nextDue || j.nextDue,
          updatedBy: userName,
          updatedAt: now,
        };
      });
    });

    // Track 1: hydration-epoch bump.
    track1.bumpHydrationEpoch();

    return completionRecord;
  }, [can, setValue, userName]);

  // ============================================================================
  // QUERIES
  // ============================================================================

  /**
   * Get single job by ID
   */
  const getById = useCallback((id) => {
    return allJobs.find(j => j.id === id) || null;
  }, [allJobs]);

  /**
   * Get active jobs only
   */
  const getActive = useCallback(() => {
    return list;
  }, [list]);

  /**
   * Get archived jobs only
   */
  const getArchived = useCallback(() => {
    return archivedJobs;
  }, [archivedJobs]);

  /**
   * Get jobs by category
   */
  const getByCategory = useCallback((category) => {
    return list.filter(j => (j.category || '').toLowerCase() === category.toLowerCase());
  }, [list]);

  /**
   * Get jobs by domain
   */
  const getByDomain = useCallback((domain) => {
    return list.filter(j => (j.domain || '').toLowerCase() === domain.toLowerCase());
  }, [list]);

  /**
   * Get custom jobs only
   */
  const getCustomJobs = useCallback(() => {
    return list.filter(j => j.custom === true);
  }, [list]);

  // ============================================================================
  // RETURN API (matches crew pattern)
  // ============================================================================

  return {
    // Data
    list,           // Active jobs only (excludes archived)
    allJobs,        // All jobs including archived
    archivedJobs,   // Archived jobs only
    loaded,         // Loading state from useStorageState

    // Mutations
    add,            // Add new job
    update,         // Update existing job
    archive,        // Soft delete (archive)
    restore,        // Restore archived job
    remove,         // Permanent delete (admin)
    addCompletion,  // Add completion record
    clearOriginatingDefectId, // FT-353f: clear defect back-reference without deleting job

    // Queries
    getById,
    getActive,
    getArchived,
    getByCategory,
    getByDomain,
    getCustomJobs,
  };
};

export default useMaintenanceJobsDomain;
